import type { CargoProps } from '@api/models/cargo';
import {
	Currencies,
	FreightRateType,
} from './constants';
import { Values } from './objectEnums';
import { convertCurrency } from './math';

export type IntakeFDDCargo = Pick<
	CargoProps,
	'quantity' |
	'quantityTolerance' |
	'freightType' |
	'freightRate' |
	'overageAmount' |
	'deadfreightAmount' |
	'currency' |
	'exchangeRate'
>;

export type IntakeFDDCalculation = {
	freightQuantity: number;
	freightAmount: number;
	overageQuantity: number;
	overageAmount: number;
	deadfreightQuantity: number;
	deadfreightAmount: number;
};

const getIntakeFDDNumbers = (
	cargo: IntakeFDDCargo | undefined,
	totalLoaded: number,
	estimateCurrency?: Values<typeof Currencies>,
): IntakeFDDCalculation => {
	if (!cargo) {
		return {
			freightQuantity: 0,
			freightAmount: 0,
			overageQuantity: 0,
			overageAmount: 0,
			deadfreightQuantity: 0,
			deadfreightAmount: 0,
		};
	}

	const {
		quantity = 0,
		quantityTolerance = 0,
		freightType,
		freightRate = 0,
		overageAmount: baseOverageAmount = 0,
		deadfreightAmount: baseDeadfreightAmount = 0,
	} = cargo;

	const cpQuantity = quantity;
	const tolerance = (quantityTolerance ?? 0) / 100;
	const cpQuantityMin = cpQuantity * (1 - tolerance);
	const cpQuantityMax = cpQuantity * (1 + tolerance);

	let freightQuantity = 0;

	if (totalLoaded > cpQuantity) {
		freightQuantity = Math.min(totalLoaded, cpQuantityMax);
	} else {
		freightQuantity = Math.min(totalLoaded, cpQuantity);
	}

	const overageQuantity = totalLoaded > cpQuantityMax ? totalLoaded - cpQuantityMax : 0;
	const deadfreightQuantity = totalLoaded < cpQuantityMin ? cpQuantityMin - totalLoaded : 0;

	const freightAmount =
		freightType === FreightRateType.LUMPSUM ? freightRate : freightQuantity * freightRate;
	const overageAmount =
		freightType === FreightRateType.LUMPSUM ?
			baseOverageAmount :
			overageQuantity * (baseOverageAmount ?? 0);
	const deadfreightAmount =
		freightType === FreightRateType.LUMPSUM ?
			baseDeadfreightAmount :
			deadfreightQuantity * (baseDeadfreightAmount ?? 0);

	const exchangeRate = estimateCurrency != null && estimateCurrency !== cargo.currency ?
		cargo.exchangeRate :
		1;

	return {
		freightQuantity,
		freightAmount: convertCurrency(freightAmount, exchangeRate),
		overageQuantity,
		overageAmount: convertCurrency(overageAmount ?? 0, exchangeRate),
		deadfreightQuantity,
		deadfreightAmount: convertCurrency(deadfreightAmount ?? 0, exchangeRate),
	};
};

export default getIntakeFDDNumbers;
